import React, { Dispatch, SetStateAction, useContext, useEffect, useState } from 'react';
import { Button, Dropdown, SplitButton } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';

import useAlert from '../../../Alerts/useAlert';
import { useHttpUtils } from '../../../Utilities/useHttpUtils';
import usePrivileges from '../../../Auth/usePrivileges';
import LotCountContext from '../../../Contexts/LotCountContext';
import { LotDetailContext } from '../../../Contexts/LotDetailContext';
import CommentDialog from "../../../Dialogs/CommentDialog";
import { LotStatus } from '../../../Types/Lot';

interface ProcessAgainButtonProps {
    setLoading: Dispatch<SetStateAction<boolean>>;
}

function ProcessAgainButton({ setLoading }: ProcessAgainButtonProps) {

    const { t } = useTranslation();
    const { setAlert } = useAlert();
    const { fetchDataAuthenticated } = useHttpUtils();
    const { updateCounters } = useContext(LotCountContext);

    const {
        lotDetail: {
            summary: {
                lotNumber,
                businessUnitCode,
                currentStatus,
                processingStatus
            } },
        getLotDetail,
        updateLotStatus,
        lotFlags: {
            canFullRecalc
        }
    } = useContext(LotDetailContext);
    const { canProcess } = usePrivileges(businessUnitCode);

    const [disabled, setDisabled] = useState<boolean>(false);
    const [showProcessAgainCommentDialog, setShowProcessAgainCommentDialog] = useState<boolean>(false);
    const [showFullRecalculateCommentDialog, setShowFullRecalculateCommentDialog] = useState<boolean>(false);

    useEffect(() => {
        switch (true) {
            case currentStatus !== "Pending" && processingStatus === "Queued":
            case currentStatus !== "Pending" && processingStatus === "Processing":
                setDisabled(true);
                break;
            default:
                setDisabled(false);
        }
    }, [currentStatus, processingStatus]);

    const handleProcessAgainCommentSave = (comment: string) => {
        updateLotStatus(LotStatus.Processing, comment);
    };

    const handleFullRecalculateCommentSave = async (comment: string) => {
        setLoading(true);
        await fetchDataAuthenticated(`/api/v1/lot/${lotNumber}/fullRecalculate`, 'PUT', JSON.stringify(comment))
            .then(async response => {
                if (response.ok) {
                    setAlert("success", "Success", `Flagged lot to fully recalculate`);
                    getLotDetail();
                    updateCounters();
                } else {
                    let errorMessage = await response.text();
                    console.log("Failed to flag lot to fully recalculate: " + errorMessage);
                    throw new Error("Failed to flag lot to fully recalculate");
                }
            })
            .catch(error => {
                setAlert("danger", "Error", error.message);
            })
            .finally(() => {
                setLoading(false);
            });
    };

    return (
        <>
            {
                canFullRecalc &&
                <SplitButton variant="primary"
                    title={t<string>('LotDetails.Summary.ProcessAgain', { context: businessUnitCode })}
                    onClick={() => setShowProcessAgainCommentDialog(true)}
                    disabled={disabled || !canProcess}
                    hidden={currentStatus === "Graded"}>
                    <Dropdown.Item onClick={() => setShowProcessAgainCommentDialog(true)}>{t<string>('LotDetails.Summary.ProcessAgain', { context: businessUnitCode })}</Dropdown.Item>
                    <Dropdown.Item onClick={() => setShowFullRecalculateCommentDialog(true)}>{t('LotDetails.Summary.FullReprocess')}</Dropdown.Item>
                </SplitButton>
            }
            {
                !canFullRecalc &&
                <Button variant="primary"
                    onClick={() => setShowProcessAgainCommentDialog(true)}
                    disabled={disabled || !canProcess}
                    hidden={currentStatus === "Graded"}>
                    {t<string>('LotDetails.Summary.ProcessAgain', { context: businessUnitCode })}
                </Button>
            }
            <CommentDialog
                showDialog={showProcessAgainCommentDialog}
                closeDialog={() => setShowProcessAgainCommentDialog(false)}
                onSave={handleProcessAgainCommentSave} />
            <CommentDialog
                showDialog={showFullRecalculateCommentDialog}
                closeDialog={() => setShowFullRecalculateCommentDialog(false)}
                onSave={handleFullRecalculateCommentSave} />
        </>
    );
}

export default ProcessAgainButton;